import { CopyOutlined, EditOutlined, InboxOutlined } from "@ant-design/icons";
import { App, Button, Card, Checkbox, Descriptions, Modal, Select, Switch, Tag, Tooltip } from "antd";
import React from "react";
import { useTranslation } from "react-i18next";
import { PRODUCT_IMAGE_PLACHOLDER } from "../common/constants";
import { useSetStoreProductOutOfStockMutation } from "../graphql/generated/hooks";
import SafeImage from "./core/SafeImage";

type Props = {
  loading?: boolean;
  name: string;
  category: string | undefined;
  price: number;
  coverImageURL?: string;
  stores?: { id: number; name: string; outOfStock?: boolean }[];
  productId?: number;
  onUpdated?: () => void;
  onCheck?: (checked: boolean) => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
};

const ProductCard: React.FC<Props> = ({
  loading = false,
  name,
  category,
  price,
  coverImageURL,
  stores = [],
  productId,
  onUpdated,
  onCheck,
  onEdit,
  onDuplicate,
}: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [open, setOpen] = React.useState<boolean>(false);
  const [selectedStoreIds, setSelectedStoreIds] = React.useState<number[]>(
    stores.filter((s) => s.outOfStock).map((s) => s.id)
  );
  const [outOfStock, setOutOfStock] = React.useState<boolean>(true);
  const [setOutOfStockMutation, { loading: saving }] =
    useSetStoreProductOutOfStockMutation();

  const actions: React.ReactNode[] = [
    onCheck ? (
      <Tooltip title={t("tooltip.select") || "Select"}>
        <Checkbox
          onChange={(value) => {
            onCheck(value.target.checked);
          }}
        />
      </Tooltip>
    ) : null,
    onDuplicate ? (
      <Tooltip title={t("button.duplicate") || "Duplicate"}>
        <CopyOutlined key="duplicate" onClick={onDuplicate} />
      </Tooltip>
    ) : null,
    onEdit ? (
      <Tooltip title={t("button.edit") || "Edit"}>
        <EditOutlined key="edit" onClick={onEdit} />
      </Tooltip>
    ) : null,
    productId ? (
      <Tooltip title={t("tooltip.stock") || "Stock"}>
        <InboxOutlined
          key="stock"
          onClick={() => {
            const defaults = stores.filter((s) => s.outOfStock).map((s) => s.id);
            setSelectedStoreIds(defaults);
            setOutOfStock(defaults.length > 0);
            setOpen(true);
          }}
        />
      </Tooltip>
    ) : null,
  ];

  return (
    <Card
      loading={loading}
      actions={actions}
      cover={
        <SafeImage
          style={{
            objectFit: "cover",
          }}
          height={150}
          alt={name}
          src={coverImageURL || PRODUCT_IMAGE_PLACHOLDER}
        />
      }
    >
      <Card.Meta
        title={name}
        description={
          <Descriptions
            layout="horizontal"
            column={1}
            items={[
              {
                label: "price",
                children: price === 0 ? t("label.free") : price,
              },
              { label: "category", children: category },
              {
                label: "stores",
                children: (
                  <div style={{ maxHeight: 120, overflowY: "auto" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {stores.map((store) => (
                        <Tag
                          color={store.outOfStock ? "red" : "blue"}
                          key={store.id}
                          style={{ width: "fit-content" }}
                        >
                          {store.name}
                          {store.outOfStock ? " (Out of stock)" : ""}
                        </Tag>
                      ))}
                    </div>
                  </div>
                ),
              },
            ]}
          />
        }
      />
      <Modal
        open={open}
        title={t("label.edit_out_of_stock") || "Edit"}
        onCancel={() => setOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>{"Out of stock"}</span>
            <Switch
              checked={outOfStock}
              onChange={(checked) => setOutOfStock(checked)}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>{"Stores"}</div>
            <Select
              style={{ width: "100%" }}
              mode="multiple"
              value={selectedStoreIds}
              onChange={(vals) => setSelectedStoreIds(vals as number[])}
              options={stores.map((s) => ({ label: s.name, value: s.id }))}
              placeholder={"Select stores"}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setOpen(false)}>{t("button.cancel") || "Cancel"}</Button>
            <Button
              type="primary"
              loading={saving}
              disabled={!productId || selectedStoreIds.length === 0}
              onClick={() => {
                if (!productId) return;
                setOutOfStockMutation({
                  variables: {
                    id: productId,
                    outOfStock,
                    storeIds: selectedStoreIds,
                  },
                })
                  .then(() => {
                    message.success(t("message.success.data_updated") || "Updated");
                    setOpen(false);
                    onUpdated && onUpdated();
                  })
                  .catch((err) => {
                    message.error(t("message.error.data_update_failed") || "Update failed");
                  });
              }}
            >
              {t("button.save") || "Save"}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default ProductCard;
